import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Box, Typography, Button, IconButton, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './../BottomSheet.css';
import { useDrag } from 'react-use-gesture';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import upIcon from '../assets/up_icon.png';
import downIcon from '../assets/down_icon.png';
import { mediatorAppFetch } from '../utils/apiFetch';
import { resolveFutComLotQtySteps } from '../utils/futcomLotQtyStep';
import { pendingOrdersCancelledNote } from '../utils/pendingOrderNotice';

const BottomSheetClosePosition = ({ userDetails, position, stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog, stocks, positions: openTrades } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const [orderExecutionType, setOrderExecutionType] = useState('Market');
  const [price, setPrice] = useState('');
  const [lots, setLots] = useState(1);
  const [lotsDraft, setLotsDraft] = useState(null);
  const [quantityDraft, setQuantityDraft] = useState(null);
  const [quantity, setQuantity] = useState(position?.buyQty > 0 ? position?.buyQty : position?.sellQty);
  const [isDepthExpanded, setIsDepthExpanded] = useState(true);
  const [positionWithLive, setPositionWithLive] = useState(position);
  const [now, setNow] = useState(() => Date.now());
  const hasDebugLoggedRef = useRef(false);

  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  const [successAlert, setSuccessAlert] = useState({ show: false, title: '', message: '' });

  const successAlertSpring = useSpring({
    opacity: successAlert.show ? 1 : 0,
    scale: successAlert.show ? 1 : 0.85,
    config: { tension: 300, friction: 25 }
  });

  const handleSuccessAlertClose = () => {
    setSuccessAlert({ show: false, title: '', message: '' });
    onClose();
  };

  // Update position with live data from stocks context - updates in real-time
  useEffect(() => {
    if (position) {
      const liveStock = stocks[position.instrument_token] || stock || {};
      setPositionWithLive({
        ...position,
        live: liveStock
      });
    }
  }, [position, stock, stocks]);

  // Debug: print actual DPR/circuit source data
  useEffect(() => {
    if (!isOpen || !position) {
      hasDebugLoggedRef.current = false;
      return;
    }

    const token = position.instrument_token;
    const liveStockFromContext = token != null ? (stocks?.[token] || {}) : {};
    const liveStockFromPositionWithLive = positionWithLive?.live || {};
    const lowerFromProp = stock?.lower_circuit ?? null;
    const upperFromProp = stock?.upper_circuit ?? null;
    const lowerFromContext = liveStockFromContext?.lower_circuit ?? null;
    const upperFromContext = liveStockFromContext?.upper_circuit ?? null;
    const lowerFromLive = liveStockFromPositionWithLive?.lower_circuit ?? null;
    const upperFromLive = liveStockFromPositionWithLive?.upper_circuit ?? null;

    if (!hasDebugLoggedRef.current) {
      console.log('[BottomSheetClosePosition][debug] instrument_token:', token);
      console.log('[BottomSheetClosePosition][debug] stock prop:', stock);
      console.log('[BottomSheetClosePosition][debug] stocks context entry:', liveStockFromContext);
      console.log('[BottomSheetClosePosition][debug] circuits -> prop:', lowerFromProp, upperFromProp, 'context:', lowerFromContext, upperFromContext, 'live:', lowerFromLive, upperFromLive);
      hasDebugLoggedRef.current = true;
    }
  }, [isOpen, position?.instrument_token, stock, stocks, positionWithLive?.live]);

  // Keeps the short-trade countdown moving so the action button reappears on its own.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  const resolvePositionLotSize = (pos) => {
    const size = Number(pos?.usedLotSize || pos?.stockLotSize || pos?.lotSize || 1);
    return size > 0 ? size : 1;
  };

  const toWholeQuantity = (value) => {
    const next = Math.round(Number(value));
    return Number.isFinite(next) ? Math.max(0, next) : 0;
  };

  const resolvePositionLotVariant = (pos) => {
    const variant = String(pos?.lotVariant || 'MEGA').toUpperCase();
    return variant === 'MINI' ? 'MINI' : 'MEGA';
  };

  // Sell positions store a negative netQty. The close limits must use the open size.
  const resolveOpenQuantity = (pos) => {
    const raw = pos?.netQty || (pos?.buyQty > 0 ? pos?.buyQty : pos?.sellQty) || 0;
    const size = Math.abs(Number(raw));
    return Number.isFinite(size) ? size : 0;
  };

  // Initialize values when position changes
  useEffect(() => {
    if (isOpen && position) {
      const netQty = resolveOpenQuantity(position);
      const lotSize = resolvePositionLotSize(position);
      setLots(netQty / lotSize);
      setLotsDraft(null);
      setQuantityDraft(null);
      setQuantity(toWholeQuantity(netQty));
      setPrice('');
      setOrderExecutionType('Market');
    }
  }, [isOpen, position]);

  const toggleDepth = () => {
    setIsDepthExpanded(!isDepthExpanded);
  };

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
  };

  const handleOrderTypeToggle = (orderExecutionType) => {
    setOrderExecutionType(orderExecutionType);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  // Get market depth
  const buyDepth = positionWithLive?.live?.depth?.buy || [];
  const sellDepth = positionWithLive?.live?.depth?.sell || [];

  // Calculate the change percentage - use live data if available
  const _Bid = positionWithLive?.live?.Bid != undefined ? positionWithLive.live.Bid : (stock?.Bid || position?.buyPrice || 0);
  const _Ask = positionWithLive?.live?.Ask != undefined ? positionWithLive.live.Ask : (stock?.Ask || position?.sellPrice || 0);
  const _Open = positionWithLive?.live?.Open != undefined ? positionWithLive.live.Open : (position?.open || 0);
  const _High = positionWithLive?.live?.High != undefined ? positionWithLive.live.High : (position?.high || 0);
  const _Low = positionWithLive?.live?.Low != undefined ? positionWithLive.live.Low : (position?.low || 0);
  const _Prev_Close = positionWithLive?.live?.Prev_Close != undefined ? positionWithLive.live.Prev_Close : (position?.close || 0);
  const _LTP = positionWithLive?.live?.LTP != undefined ? positionWithLive.live.LTP : (stock?.lastTradedPrice || position?.lastTradedPrice || 0);

  const Change_per = _Prev_Close > 0 ? ((_LTP - _Prev_Close) / _Prev_Close * 100).toFixed(2) : '0.00';
  const Change = (_LTP - _Prev_Close).toFixed(2);
  const changeColor = Change > 0 ? "green" : "red";

  // Daily Price Range source aligned with BottomSheetPortfolio:
  // primary source is `position.lower_circuit` / `position.upper_circuit`.
  // keep fallbacks for safety when position misses these fields.
  const lowerCircuit =
    position?.lowerCircuit ??
    positionWithLive?.live?.lower_circuit ??
    stock?.lower_circuit ??
    0;
  const upperCircuit =
    position?.upperCircuit ??
    positionWithLive?.live?.upper_circuit ??
    stock?.upper_circuit ??
    0;

  // MySQL datetimes ("YYYY-MM-DD HH:MM:SS") are server local time, so parse them as local.
  const parseExecutionTime = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw || raw.startsWith('0000-00-00')) return null;
    const parsed = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const shortTimeMinutes = Math.max(0, Number(userDetails?.shortTime) || 0);

  // entryTime is the authoritative open time; exeDateTime covers rows that predate it.
  const resolveTradeOpenTime = (record) =>
    parseExecutionTime(record?.entryTime) || parseExecutionTime(record?.exeDateTime);

  // Newest open trade on this script decides the window, so adding a fresh lot re-arms it.
  const positionOpenedAt = (() => {
    const direct = resolveTradeOpenTime(position);
    if (direct) return direct;

    const trades = Array.isArray(openTrades) ? openTrades : [];
    return trades.reduce((latest, trade) => {
      if (String(trade?.stockId ?? '') !== String(position?.stockId ?? '')) return latest;
      if (String(trade?.exchange ?? '') !== String(position?.exchange ?? '')) return latest;
      const openedAt = resolveTradeOpenTime(trade);
      if (!openedAt) return latest;
      return !latest || openedAt > latest ? openedAt : latest;
    }, null);
  })();

  const isPositionInProfit = position?.buyQty > 0
    ? Number(_Bid) > 0 && Number(_Bid) > Number(position?.avgBuyPrice || 0)
    : Number(_Ask) > 0 && Number(position?.avgSellPrice || 0) > Number(_Ask);

  const minutesSinceOpen = positionOpenedAt ? (now - positionOpenedAt.getTime()) / 60000 : null;
  const isShortTradeBlocked = shortTimeMinutes > 0
    && isPositionInProfit
    && minutesSinceOpen !== null
    && minutesSinceOpen < shortTimeMinutes;

  const createOrder = async () => {
    setDisable(true);

    if (isShortTradeBlocked) {
      alert(`Cannot close a position in profit within ${shortTimeMinutes} minutes of opening`);
      setDisable(false);
      return;
    }

    // Determine order type based on position side
    const orderType = position.buyQty > 0 ? 2 : 1; // If long position, sell to close; if short position, buy to close
    
    // Use current state values for quantity and lots (user may have adjusted them)
    const orderQuantity = Math.abs(quantity);
    const orderLots = lots;
    
    if (orderQuantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);
      return;
    }

    if (_Bid <= 0 || _Ask <= 0 || _Bid == _Ask) {
      alert("Price not updated, please refresh");
      setDisable(false);
      return;
    }

    if(orderExecutionType=="Limit" || orderExecutionType=="SL"){
      if(Number(price) < Number(lowerCircuit) || Number(price) > Number(upperCircuit)) {
        alert("Order price is beyond daily price range.");
        setDisable(false);
        return;
      }
    }

    if (orderExecutionType=="Limit") {
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if (orderType === 1) {
        if (price >= _Bid) {
          alert("Order price must be less than bid price.");
          setDisable(false);
          return;
        }
      } else {
        if (price <= _Ask) {
          alert("Order price must be greater than ask price.");
          setDisable(false);
          return;
        }
      }
    }
    else if (orderExecutionType=="SL") { 
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if(orderType==2){
        if(price >= _Bid){
          alert("Order price should be less than bid price."); 
          setDisable(false);
          return;
        }
      }
      else {
        if(price <= _Ask){
          alert("Order price should be greater than ask price."); 
          setDisable(false);
          return;
        }
      }
    }

    if (orderLots <= 0) {
      alert("Problem with lot quantity.");
      setDisable(false);
      return;
    }

    const lotVariant = resolvePositionLotVariant(position);
    const effectiveLotSize = resolvePositionLotSize(position);

    // Prepare position data for API (keep lotVariant/usedLotSize so close hits the correct row)
    const positionData = {
      ...position,
      lotVariant,
      usedLotSize: effectiveLotSize,
      stockLotSize: effectiveLotSize,
      lotSize: effectiveLotSize,
      dispName: position.dispname || position.stockName,
      stock: {
        instrumentIdentifier: position.stockId,
        exchange: position.exchange,
        dispName: position.stockName,
        ...stock,
        // Position lot meta must win over live stock.lotsize (mega)
        lotVariant,
        usedLotSize: effectiveLotSize,
        stockLotSize: effectiveLotSize,
        lotSize: effectiveLotSize,
      }
    };

    try {
      const response = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock: positionData,
          orderTypeBuySell: orderType,
          orderExecutionType: orderExecutionType=="Market" ? 1 : (orderExecutionType=="Limit" ? 2 : 3),
          lots: Number(orderLots),
          quantity: Math.floor(orderQuantity),
          entryPrice: orderType==1 ? positionData.avgSellPrice : positionData.avgBuyPrice,
          orderTransactionType: 2,
          orderDevice: 1,
          limitSlPrice: price,
          lotVariant,
          ip: process.env.REACT_APP_USER_IP,
        }),
      });
      
      const data = await response.json();

      if (!data.success) {
        let msg = data.message;
        
        if(data.message=="SL Position"){
          msg = "Please remove SL from trades.";
        }
        else if(data.message=="Position already closed."){
          msg = "Position already closed.";
        } else {
          msg = data.message;
        }

        showToast(3, msg);

        var dataa = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: positionData.stockId,
          stockName: '"'+positionData.stockName+'"',
          stockExpiry: '"'+positionData.expiry+'"',
          orderTransactionType: 2,
          orderExecutionType: orderExecutionType=="Market" ? 1 : (orderExecutionType=="Limit" ? 2 : 3),
          buy_sell: orderType,
          lot: Number(orderLots),
          quantity: Math.floor(orderQuantity),
          remark: '"'+msg+'"',
          orderPrice: (orderExecutionType=="Limit" || orderExecutionType=="SL") ? price : (orderType==1 ? positionData.avgSellPrice : positionData.avgBuyPrice),
          lotVariant: positionData.lotVariant
        });
  
        submitRejectionLog(dataa);

        setDisable(false);
      } else {
        setDisable(false);

        const b_s = orderType == 1 ? 'Buy' : 'Sell';
        const msg = b_s + " " + Number(orderLots).toFixed(2) + " Lot(s) of " + (position.dispname || position.stockName) + " @" + Number(data.orderPrice).toFixed(2) + pendingOrdersCancelledNote(data.message);

        refreshTradesAndPortfolio('both');

        const title = (orderExecutionType === 'Limit' || orderExecutionType === 'SL')
          ? orderExecutionType + " Order Successful"
          : "Order Successful";
        setSuccessAlert({ show: true, title, message: msg });
      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  // Calculate max values based on current position
  const maxQuantity = toWholeQuantity(resolveOpenQuantity(position));
  const lotSize = resolvePositionLotSize(position);
  const lotVariant = resolvePositionLotVariant(position);
  const maxLots = maxQuantity / lotSize;

  const isFutCom = position?.exchange === 'FUTCOM';
  const {
    lotStep,
    minLots,
    quantityStep,
  } = resolveFutComLotQtySteps({
    exchange: position?.exchange,
    scriptName:
      stock?.scriptName ||
      position?.scriptName ||
      position?.stockName ||
      position?.dispname,
    lotSize,
    userDetails,
  });
  const lotsForOneQty = lotSize > 0 ? 1 / lotSize : 1;
  const minLotsAllowed = Math.min(
    maxLots,
    Math.max(isFutCom ? minLots : lotsForOneQty, lotsForOneQty)
  );
  const minQuantity = maxQuantity >= 1 ? 1 : maxQuantity;

  const capLotsToOpen = (value) => {
    let next = value;
    if (next > maxLots) next = maxLots;
    next = Number(next.toFixed(2));
    if (next > maxLots) next = Math.floor(maxLots * 100) / 100;
    return Number(next.toFixed(2));
  };

  const applyLots = (value) => {
    let next = capLotsToOpen(Math.max(minLotsAllowed, value));
    let nextQty = Math.min(maxQuantity, toWholeQuantity(next * lotSize));
    if (minQuantity >= 1 && nextQty < minQuantity) {
      nextQty = Math.min(maxQuantity, minQuantity);
      next = lotSize > 0 ? Number((nextQty / lotSize).toFixed(2)) : next;
      if (next > maxLots) next = capLotsToOpen(maxLots);
    }
    setLots(next);
    setLotsDraft(null);
    setQuantityDraft(null);
    setQuantity(nextQty);
    return next;
  };

  const handleLotsChange = (e) => {
    let raw = String(e.target.value).replace(/[^\d.]/g, '');
    const dot = raw.indexOf('.');
    if (dot !== -1) {
      raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    }

    // Keep an incomplete value so the user can clear the field and type a new number.
    if (raw === '' || raw === '.') {
      setLotsDraft(raw);
      setQuantityDraft(null);
      setLots(0);
      setQuantity(0);
      return;
    }

    let value = parseFloat(raw);
    if (!Number.isFinite(value)) {
      setLotsDraft(raw);
      return;
    }

    if (value > maxLots) {
      value = capLotsToOpen(value);
      setLots(value);
      setLotsDraft(value.toFixed(2));
      setQuantityDraft(null);
      setQuantity(Math.min(maxQuantity, toWholeQuantity(value * lotSize)));
      return;
    }

    setLotsDraft(raw);
    setQuantityDraft(null);
    setLots(value);
    setQuantity(Math.min(maxQuantity, toWholeQuantity(value * lotSize)));
  };

  const commitLotsDraft = () => {
    if (lotsDraft === null) return;
    const value = parseFloat(lotsDraft);
    applyLots(Number.isFinite(value) ? value : minLotsAllowed);
  };

  const applyQuantityValue = (value) => {
    let next = toWholeQuantity(value);
    if (next > maxQuantity) next = maxQuantity;
    if (next < minQuantity) next = minQuantity;
    setQuantity(next);
    setLots(lotSize > 0 ? Number((next / lotSize).toFixed(2)) : 0);
    setQuantityDraft(null);
    setLotsDraft(null);
  };

  const handleQuantityChange = (e) => {
    const raw = String(e.target.value).replace(/[^\d]/g, '');
    setLotsDraft(null);

    if (raw === '') {
      setQuantityDraft('');
      setQuantity(0);
      setLots(0);
      return;
    }

    let value = parseInt(raw, 10);
    if (!Number.isFinite(value)) {
      setQuantityDraft(raw);
      return;
    }

    if (value > maxQuantity) {
      value = maxQuantity;
      setQuantityDraft(String(value));
      setQuantity(value);
      setLots(lotSize > 0 ? Number((value / lotSize).toFixed(2)) : 0);
      return;
    }

    setQuantityDraft(raw);
    setQuantity(value);
    setLots(lotSize > 0 ? Number((value / lotSize).toFixed(2)) : 0);
  };

  const commitQuantityDraft = () => {
    if (quantityDraft === null) return;
    const value = parseInt(quantityDraft, 10);
    applyQuantityValue(Number.isFinite(value) ? value : minQuantity);
  };

  const incrementLots = () => {
    applyLots(Number(lots) + lotStep);
  };

  const decrementLots = () => {
    if (Number(lots) > minLotsAllowed) {
      applyLots(Number(lots) - lotStep);
    }
  };

  const incrementQuantity = () => {
    applyQuantityValue(Number(quantity) + quantityStep);
  };

  const decrementQuantity = () => {
    applyQuantityValue(Number(quantity) - quantityStep);
  };

  if (!position) return null;

  // Determine order type based on position side
  const orderType = position.buyQty > 0 ? 2 : 1; // If long position, sell to close; if short position, buy to close

  return (
    <>
      <ToastContainer />
      {successAlert.show && (
        <animated.div
          className={`success-alert-overlay ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          style={{ opacity: successAlertSpring.opacity }}
          data-theme={isDarkMode ? 'dark' : 'light'}
          onClick={e => e.stopPropagation()}
        >
          <animated.div
            className="success-alert-box"
            style={{
              transform: successAlertSpring.scale.to(s => `scale(${s})`),
              opacity: successAlertSpring.opacity
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="success-alert-icon-wrap">
              <CheckCircleIcon className="success-alert-icon" />
            </div>
            <Typography variant="h6" className="success-alert-title">{successAlert.title}</Typography>
            <Typography className="success-alert-message">{successAlert.message}</Typography>
            <Button
              variant="contained"
              className="success-alert-btn"
              onClick={handleSuccessAlertClose}
              fullWidth
            >
              OK
            </Button>
          </animated.div>
        </animated.div>
      )}
      {isOpen && <div className="backdrop" onClick={handleBackdropClick} />}
      {isOpen && (
        <animated.div style={{ styles }} className={`bottom-sheet ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
          <Box className="bottom-sheet-header">
            <Typography variant="h6" className='bottom-sheet-scriptName'>
              <p>
                <span style={{fontWeight:'600'}}>
                  {String(position.dispname || position.stockName || '').replace(/ MINI$/i, '')}
                  {lotVariant === 'MINI' && <span className="lot-variant-mini">MINI</span>}
                </span> <span className='stock-expiry'>{position.expiry}</span>
              </p>
              <p>
                <span className='bottom-sheet-exchange'>{position.exchange}</span>
                <span className='bottom-sheet-exchange'>QTY {toWholeQuantity(resolveOpenQuantity(position))}</span>
              </p>
            </Typography>
          
            <p style={{fontSize:'14px', fontWeight:'500'}}>
              <span style={{ position:' absolute', right: '20px', fontWeight: '600'}}>
                ₹{Number(_LTP).toFixed(2)}
                {Change > 0 ? (
                    <img src={upIcon} alt="Up" className="change-icon" />
                ) : (
                    <img src={downIcon} alt="Down" className="change-icon" />
                )}
              </span> <br/> 
              <span className={changeColor}>{Change}({Change_per}{'%'})</span>
            </p>
          </Box>
          
          <Box className="bottom-sheet-content">
            <Grid container spacing={2} className="stock-details">
              <Grid item xs={3} className='center'>
                <Typography>Open</Typography>
                <Typography className='ohlc'>{Number(_Open).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={3} className='center'>
                <Typography>High</Typography>
                <Typography className='ohlc'>{Number(_High).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={3} className='center'>
                <Typography>Low</Typography>
                <Typography className='ohlc'>{Number(_Low).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={3} className='center'>
                <Typography>Close</Typography>
                <Typography className='ohlc'>{Number(_Prev_Close).toFixed(2)}</Typography>
              </Grid>
            </Grid>

            {position.exchange!='OPTSTK' && <div className="stock-details dpr" style={{marginTop:'3px !important'}}>
              <p style={{textAlign:'center', fontWeight:'600', fontSize:'12px'}}>Day Price Range: { lowerCircuit || 0 } - { upperCircuit || 0 }</p>
            </div>}

            {buyDepth.length > 0 && (
              <>
                <Typography variant="h6" className='market-depth-title' onClick={toggleDepth} style={{ cursor: 'pointer', display: 'flex', justifyContent:'space-between' }}>
                  <span style={{fontWeight:600}}>Market Depth</span>
                  <span style={{fontWeight:600}}>Balance: {Number(userDetails.availableMargin || 0).toFixed(2)}</span>
                </Typography>
                {isDepthExpanded && (
                  <div className='market-depth'>
                    <Box className="order-depth">
                      <TableContainer component={Paper} className='noborder' style={{ backgroundColor: 'inherit' }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell className='left-align padding5'>Qty</TableCell>
                              <TableCell className='right-align padding5'>Buy Price</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {buyDepth.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className='left-align padding5 noborder'>{row.quantity}</TableCell>
                                <TableCell className='green right-align padding5 noborder'>{Number(row.price).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    <Box className="order-depth">
                      <TableContainer component={Paper} className='noborder' style={{ backgroundColor: 'inherit' }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell className='left-align padding5'>Sell Price</TableCell>
                              <TableCell className='right-align padding5'>Qty</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sellDepth.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className='red left-align padding5 noborder'>{Number(row.price).toFixed(2)}</TableCell>
                                <TableCell className='right-align padding5 noborder'>{row.quantity}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
            </div>
                )}
              </>
            )}

            <Box className="lot-quantity-section">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} className='center'>
                  <Typography>Lots</Typography>
                  <div style={{textAlign:'center'}}>
                    <Button 
                      className='plus-minus_btn' 
                      onClick={decrementLots}
                    >-</Button>
                    <input
                      inputMode="decimal"
                      value={lotsDraft !== null ? lotsDraft : Number(lots).toFixed(2)}
                      onChange={handleLotsChange}
                      onBlur={commitLotsDraft}
                      min="0"
                      max={maxLots}
                      step="0.1"
                      className='input-text-field'
                    />
                    <Button 
                      className='plus-minus_btn' 
                      onClick={incrementLots}
                    >+</Button>
              </div>
                </Grid>
                <Grid item xs={6} className='center'>
                  <Typography>Quantity</Typography>
                  <div style={{textAlign:'center'}}>
                    <Button 
                      className='plus-minus_btn' 
                      onClick={decrementQuantity}
                    >-</Button>
                    <input
                      inputMode="numeric"
                      value={quantityDraft !== null ? quantityDraft : toWholeQuantity(quantity)}
                      onChange={handleQuantityChange}
                      onBlur={commitQuantityDraft}
                      min={minQuantity}
                      max={maxQuantity}
                      step={quantityStep}
                      className='input-text-field'
                    />
                    <Button 
                      className='plus-minus_btn' 
                      onClick={incrementQuantity}
                    >+</Button>
            </div>
                </Grid>
              </Grid>
            </Box>

            {position.exchange!='OPTSTK' && <Box className="order-type-section" style={{display:'flex'}}>
              <div style={{ textAlign:'center', margin:'3px auto'}}>
                <div className="toggle-switch" style={{ width:'215px' }}>
                  <span style={{ width:'79px' }} className={orderExecutionType=="Market" ? 'active' : ''} onClick={() => handleOrderTypeToggle("Market")}>Market</span>
                  <span style={{ width:'70px' }} className={orderExecutionType=="Limit" ? 'active' : ''} onClick={() => handleOrderTypeToggle("Limit")}>Limit</span>
                  <span style={{ width:'65px' }} className={orderExecutionType=="SL" ? 'active' : ''} onClick={() => handleOrderTypeToggle("SL")}>SL</span>
              </div>
            </div>

              {orderExecutionType!='Market' && (
                <div style={{ textAlign:'center'}}>
                  <input 
                    type="number"
                    label="Price"
                    variant="outlined"
                    size="small"
                    value={price}
                    placeholder='Price'
                    className='input-text-field full-width-important'
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
              )}
            </Box>}
          
            <Box className="bottom-actions">
              {isShortTradeBlocked ? (
                <Typography
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#b45309',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(180, 83, 9, 0.35)',
                    backgroundColor: 'rgba(180, 83, 9, 0.08)'
                  }}
                >
                  Cannot close a position in profit within {shortTimeMinutes} minutes of opening
                </Typography>
              ) : (
                <Button 
                  disabled={disableBtn || quantity <= 0} 
                  variant="contained" 
                  className={orderType === 1 ? 'button-buy' : 'button-sell'} 
                onClick={createOrder}
                  style={{ width: '100%' }}
                >
                  {orderType === 1 ? 'Buy' : 'Sell'} {toWholeQuantity(quantity)} QTY
                </Button>
              )}
            </Box>
          </Box>
        </animated.div>
      )}
    </>
  );
};

export default BottomSheetClosePosition;
